import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Info } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { formatUSD } from "@/lib/format";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  PageContainer,
  PageHeader,
  Skeleton,
  StatusPill,
  LEAD_STATUS,
} from "./dealerUi";

const RANGE_OPTIONS = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "ytd", label: "Year to date" },
];

const CHART_COLORS = ["#1B3A6B", "#D4A537", "#3b82f6", "#10b981", "#f97316", "#8b5cf6"];

const FUNNEL_ORDER = ["new", "contacted", "appt_scheduled", "showed_up", "sold"];

const KPIBox = ({ label, value, sub, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <p className="text-xs uppercase font-semibold tracking-widest text-slate-500">{label}</p>
    <p className="mt-1 font-display text-2xl font-semibold text-slate-900">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
  </div>
);

/**
 * Real-time billing preview panel. Fetches /dealer/billing/status so dealers
 * see charge-status splits (succeeded / pending / failed) live — bright
 * feedback loop that reduces surprise when invoices land.
 *
 * Phase 11 — payment-processor wording stripped from the dealer view; dealers
 * shouldn't care which rails we clear on.
 */
const BillingPreviewPanel = () => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/dealer/billing/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);
  if (!status) return null;
  const pending = status.pending_charges_this_month || 0;
  const succeeded = status.succeeded_charges_this_month || 0;
  const failed = status.failed_charges_this_month || 0;
  const total = pending + succeeded + failed;
  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="analytics-billing-preview">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-brand/10 text-brand flex items-center justify-center">
            <CreditCard size={18} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Live monthly charges — {status.month_label}</p>
            <p className="text-sm text-slate-600 mt-0.5">Real-time cash-in vs pending totals for showed-up appointments.</p>
          </div>
        </div>
        <Link to="/dealer/billing" className="text-brand text-sm font-semibold hover:underline" data-testid="analytics-open-billing-link">Open Billing →</Link>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total events</p>
          <p className="mt-1 font-display text-2xl text-slate-900">{total}</p>
          <p className="text-xs text-slate-500">showed-up appts</p>
        </div>
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
          <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Charged</p>
          <p className="mt-1 font-display text-2xl text-emerald-700">{formatUSD((status.succeeded_amount_cents || 0) / 100, 0)}</p>
          <p className="text-xs text-emerald-700/80">{succeeded} succeeded</p>
        </div>
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
          <p className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Pending</p>
          <p className="mt-1 font-display text-2xl text-amber-700">{formatUSD((status.pending_amount_cents || 0) / 100, 0)}</p>
          <p className="text-xs text-amber-700/80">{pending} in queue</p>
        </div>
        <div className={`rounded-lg border p-3 ${failed > 0 ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200"}`}>
          <p className={`text-[10px] uppercase tracking-widest font-semibold ${failed > 0 ? "text-rose-700" : "text-slate-500"}`}>Failed</p>
          <p className={`mt-1 font-display text-2xl ${failed > 0 ? "text-rose-700" : "text-slate-400"}`}>{formatUSD((status.failed_amount_cents || 0) / 100, 0)}</p>
          <p className={`text-xs ${failed > 0 ? "text-rose-700/80" : "text-slate-500"}`}>{failed} to resolve</p>
        </div>
      </div>
    </div>
  );
};

const DealerAnalytics = () => {
  const [range, setRange] = useState("30d");
  const [overview, setOverview] = useState(null);
  const [topVehicles, setTopVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: ov }, { data: tv }] = await Promise.all([
        api.get("/dealer/analytics/overview", { params: { range } }),
        api.get("/dealer/analytics/top-vehicles", { params: { range, limit: 10 } }),
      ]);
      setOverview(ov);
      setTopVehicles(tv);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  if (loading || !overview) {
    return (
      <PageContainer>
        <PageHeader
          title="Analytics"
          subtitle="Loading dealership performance…"
          testId="dealer-analytics-header"
        />
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64 mt-6" />
      </PageContainer>
    );
  }

  const funnelData = FUNNEL_ORDER.map((k, idx) => ({
    stage: LEAD_STATUS[k].label,
    count: overview.conversion_funnel?.[k] || 0,
    color: CHART_COLORS[idx % CHART_COLORS.length],
  }));

  const sourceData = Object.entries(overview.leads_by_source || {}).map(([source, count], i) => ({
    name: source.replace("_", " "),
    value: count,
    color: CHART_COLORS[i % CHART_COLORS.length],
  }));

  const conversionPct = overview.leads_total
    ? Math.round(((overview.sold_count || 0) / overview.leads_total) * 100)
    : 0;

  return (
    <PageContainer>
      <PageHeader
        title="Analytics"
        subtitle="Track leads, appointment outcomes and revenue over time."
        testId="dealer-analytics-header"
        right={
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-44" data-testid="analytics-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4" data-testid="analytics-kpis">
        <KPIBox label="Total leads" value={overview.leads_total} testId="analytics-kpi-leads" />
        <KPIBox
          label="Showed up"
          value={overview.showed_up_count}
          sub={`${overview.no_show_count} no-show · ${overview.cancelled_count} cancelled`}
          testId="analytics-kpi-showed-up"
        />
        <KPIBox
          label="Sold"
          value={overview.sold_count}
          sub={`${conversionPct}% overall close`}
          testId="analytics-kpi-sold"
        />
        <div
          className="rounded-xl border border-slate-200 bg-white p-4 relative"
          data-testid="analytics-billing"
        >
          <div className="flex items-center gap-1.5">
            <p className="text-xs uppercase font-semibold tracking-widest text-slate-500">
              Estimated billing
            </p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="text-slate-400 hover:text-slate-600" data-testid="analytics-billing-tooltip">
                    <Info size={12} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                  Billing is finalized at month end. Only &quot;Showed Up&quot; appointments count. No monthly fees.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="mt-1 font-display text-2xl font-semibold text-slate-900">
            {formatUSD(overview.estimated_billing)}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            {overview.showed_up_count} × $300
          </p>
        </div>
      </div>

      {/* Real-time billing preview panel (Phase 5) */}
      <BillingPreviewPanel />

      {/* Charts row */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Funnel */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-4" data-testid="analytics-funnel-card">
          <h3 className="font-display text-base font-semibold text-slate-900 mb-3">
            Conversion funnel
          </h3>
          {overview.leads_total ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={funnelData} layout="vertical" margin={{ left: 40, right: 24, top: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 12, fill: "#64748b" }} />
                <YAxis dataKey="stage" type="category" tick={{ fontSize: 12, fill: "#334155" }} width={130} />
                <RTooltip />
                <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                  {funnelData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-500 py-16 text-center">No lead activity in this range.</p>
          )}
        </div>

        {/* Sources */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="analytics-sources-card">
          <h3 className="font-display text-base font-semibold text-slate-900 mb-3">
            Lead sources
          </h3>
          {sourceData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={sourceData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80}>
                  {sourceData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <RTooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-500 py-16 text-center">No sources tracked yet.</p>
          )}
        </div>
      </div>

      {/* Time series */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="analytics-timeseries-card">
        <h3 className="font-display text-base font-semibold text-slate-900 mb-3">
          Leads over time
        </h3>
        {overview.time_series_leads?.length ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={overview.time_series_leads}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
              <RTooltip />
              <Line type="monotone" dataKey="count" stroke="#1B3A6B" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-slate-500 py-10 text-center">No leads recorded in this window.</p>
        )}
      </div>

      {/* Top vehicles */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="analytics-top-vehicles-card">
        <h3 className="font-display text-base font-semibold text-slate-900 mb-3">
          Top-performing vehicles ({range})
        </h3>
        {topVehicles.length ? (
          <div className="divide-y divide-slate-100">
            {topVehicles.map((v) => (
              <div key={v.id} className="flex items-center gap-3 py-2">
                <div className="w-14 h-10 rounded-md overflow-hidden bg-slate-100 flex-shrink-0">
                  {v.images?.[0] && (
                    <img src={v.images[0]} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">
                    {v.year} {v.make} {v.model} {v.trim || ""}
                  </p>
                  <p className="text-xs text-slate-500">
                    {formatUSD(v.price)} · {v.views_count} views · {v.total_lead_count} total leads
                  </p>
                </div>
                <StatusPill tone="blue">{v.leads_in_range} leads</StatusPill>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 py-6 text-center">No lead activity in this window.</p>
        )}
      </div>
    </PageContainer>
  );
};

export default DealerAnalytics;
